import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Modal, Typography } from "@mui/material";
import { Feature } from "geojson";
import { EVENT_TYPES, FALLBACK_ALERT } from "./WeatherAlertsUtils";
import { AlertHeaderBanner } from "./AlertHeaderBanner";
import { AlertInfoTable } from "./AlertInfoTable";
import { AlertFooterControls } from "./AlertFooterControls";

type WeatherAlertModalProps = {
    alerts: Array<Feature>,
    open: boolean,
    onClose: () => void,
};

export const WeatherAlertModal: React.FC<WeatherAlertModalProps> = ( {
    alerts,
    open,
    onClose
} ) =>
{
    const [ currentIndex, setCurrentIndex ] = useState( 0 );
    const [ showFullDescription, setShowFullDescription ] = useState( false );
    const [ showFullInstruction, setShowFullInstruction ] = useState( false );

    useEffect( () =>
    {
        setCurrentIndex( 0 );
    }, [ open ] );

    if ( alerts === undefined || alerts.length === 0 ) return null;

    const alert = alerts[currentIndex];
    const props = alert.properties || {};
    const headline = props.headline ?? props.event ?? "Weather Alert";

    const eventType = props.event as keyof typeof EVENT_TYPES;
    const eventConfig = EVENT_TYPES[eventType] ?? FALLBACK_ALERT;
    const borderColor = eventConfig.color ?? "#b00020";

    const handlePrev = () => setCurrentIndex( prev => ( prev === 0 ? alerts.length - 1 : prev - 1 ) );
    const handleNext = () => setCurrentIndex( prev => ( prev === alerts.length - 1 ? 0 : prev + 1 ) );

    const truncateText = ( text: string, limit: number ) =>
    {
        if ( text.length <= limit ) return text;
        return text.slice( 0, limit ) + "...";
    };

    const charLimit = 100;

    return (
        <Modal
            open = {open}
            onClose = {onClose}
            aria-labelledby = "weather-alert-modal-title"
            aria-describedby = "weather-alert-modal-description"
        >
            <Box
                sx = {{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 500 },
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    border: `4px solid ${borderColor}`,
                    maxHeight: "80vh",
                    overflowY: "auto",
                }}
            >
                <AlertHeaderBanner eventType = {eventType} color = {borderColor}/>
                <Divider sx = {{ my: 2 }}/>

                <AlertInfoTable
                    headline = {headline}
                    severity = {props?.severity}
                    urgency = {props?.urgency}
                    certainty = {props?.certainty}
                />

                {props.description !== undefined && (
                    <Box mt = {2}>
                        <Typography variant = "body2">
                            <strong>Description:</strong>{" "}
                            {showFullDescription
                                ? props.description
                                : truncateText( props.description, charLimit )}{" "}
                            {props.description.length > charLimit && (
                                <Button
                                    variant = "text"
                                    size = "small"
                                    onClick = {() => setShowFullDescription( !showFullDescription )}
                                    sx = {{
                                        textTransform: "none",
                                        padding: 0,
                                        minWidth: "auto",
                                        ml: 1,
                                        fontSize: "0.875rem",
                                        verticalAlign: "baseline",
                                    }}
                                >
                                    {showFullDescription ? "Less" : "More"}
                                </Button>
                            )}
                        </Typography>
                    </Box>
                )}

                {props.instruction !== undefined && (
                    <Box mt = {1}>
                        <Typography variant = "body2">
                            <strong>Instruction:</strong>{" "}
                            {showFullInstruction
                                ? props.instruction
                                : truncateText( props.instruction, charLimit )}{" "}
                            {props.instruction.length > charLimit && (
                                <Button
                                    variant = "text"
                                    size = "small"
                                    onClick = {() => setShowFullInstruction( !showFullInstruction )}
                                    sx = {{
                                        textTransform: "none",
                                        padding: 0,
                                        minWidth: "auto",
                                        ml: 1,
                                        fontSize: "0.875rem",
                                        verticalAlign: "baseline",
                                    }}
                                >
                                    {showFullInstruction ? "Less" : "More"}
                                </Button>
                            )}
                        </Typography>
                    </Box>
                )}

                {alerts.length > 1 && (
                    <Box mt = {3}>
                        <AlertFooterControls
                            alertsCount = {alerts.length}
                            currentIndex = {currentIndex}
                            onPrev = {handlePrev}
                            onNext = {handleNext}
                        />
                    </Box>
                )}

                <Box mt = {3} textAlign = "right">
                    <Button onClick = {onClose} variant = "contained" color = "primary">
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
